import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Breadcrumbs from "@/components/Breadcrumbs";
import usePageMeta from "@/hooks/use-page-meta";
import { Calendar, ArrowUpRight, User } from "lucide-react";
import { BLOGS, BRAND } from "@/constants/content";

export default function BlogsPage() {
  usePageMeta({
    title: "KinFlex Health Blogs — Home Healthcare, Elder Care & Recovery",
    description: "Practical guides on home ICU setup, elder care in Delhi NCR, post-op recovery and home healthcare best practices from KinFlex's clinical team.",
    canonical: `https://${BRAND.domain}/blogs`,
  });
  const fmt = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  return (
    <Layout>
      <section className="pt-28 pb-6 md:pt-36 md:pb-12 bg-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-medical-grid opacity-60 pointer-events-none" />
        <div className="container-page relative max-w-4xl">
          <Breadcrumbs items={[{ label: "Blogs" }]} />
          <div className="mt-5 eyebrow">Health Blogs</div>
          <h1 className="mt-2 font-display font-bold text-slate-900 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Wisdom from our <span className="bg-gradient-to-r from-[#0A84FF] to-[#10B981] bg-clip-text text-transparent">clinical team</span>.
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">
            Practical guides for home healthcare, elder care, post-op recovery — written and reviewed by KinFlex's clinical team.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="container-page grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {BLOGS.map((b) => (
            <Link key={b.slug} to={`/blogs/${b.slug}`} className="group rounded-2xl overflow-hidden border border-slate-200/70 bg-white hover:shadow-xl hover:-translate-y-1 transition-all" data-testid={`blog-list-${b.slug}`}>
              <div className="aspect-[16/10] overflow-hidden">
                <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmt(b.date)}</span>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {b.author}</span>
                  {b.tags?.[0] && (<><span>·</span><span className="font-medium" style={{ color: '#0A84FF' }}>{b.tags[0]}</span></>)}
                </div>
                <h3 className="mt-3 font-display font-semibold text-slate-900 text-lg leading-snug group-hover:text-[#0A84FF] transition-colors">
                  {b.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 line-clamp-2 leading-relaxed">{b.excerpt}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A84FF] group-hover:gap-2.5 transition-all">
                  Read article <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </Layout>
  );
}
